// screens/Mail.tsx — S1 (COCKPIT-ASSISTANT-SPEC.md §S1, decisions 4297868b /
// b23b0619 / 94fda767). Build to mockup-v10/Main.dc.html (plate 01, open)
// and Folded.dc.html (plate 02, folded) — see
// /home/ubuntu/cockpit-build/S1-MAIL-SCREEN-BRIEF.md.
//
// Three columns at >=900 (reusing the shell's own COCKPIT_BAR_MAX — the
// mockup is fixed at 1440 and gives no second breakpoint of its own, so
// this screen's fold and the Atlas column's bar/column switch share the
// ONE real number rather than drifting apart): the accounts strip (Mist),
// the message list, the reading pane. Below that: accounts -> list ->
// message, one pane at a time, with the reply as a bottom sheet — the
// folded flow plate 02 draws.
//
// ⚑ THE RECORDED DRAFT'S BODY (core/mail-draft-read, atlas-core
// 2026-10-15; not yet on main — atlas-review merges it before this
// branch). GET /api/threads/:id's `gmail.draft` carries only {id,
// connectionId, bodyHash, origin} — a HASH, never the text. The text
// comes from GET /api/mail/drafts/:id (getMailDraft), fetched when the
// reply sheet opens on a thread that has one: the body prefills the
// composer (editable; From stays locked), To/Subject show read-only, and
// `changedInGmail` — true when the draft was edited on Gmail's side since
// it was recorded — gets its own named line beside the composer, never a
// block: Send always sends exactly what the sheet shows. A 409
// draft_changed (the draft is gone from Gmail entirely) shows the
// server's sentence with a Reload door. No fabricated text ever fills the
// composer while that read is in flight or has failed — see the loading/
// error branches in replySheet below.
//
// ⚑ MARK-READ'S OWN DOOR (a judgement call, reported in the builder's final
// message). The mockup shows no separate "mark read" control — only
// Reply/Forward/Archive in the reading pane's footer — so opening an
// unread thread marks it read as the natural consequence (the universal
// mail convention, and what "mark-read from the reading pane" means absent
// a drawn control). The list ALSO gets a real, explicit door the mockup
// doesn't draw: tapping a row's unread dot toggles read/unread without
// opening the thread — "mark-read from the list" as its own act, not just
// a side effect of selection. The reading pane also carries an explicit
// Mark unread/Mark read toggle, so a read can be corrected without Gmail.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  getMailAccounts,
  getMailDraft,
  getMailThreads,
  getThread,
  saveMailCompose,
  sendMailCompose,
  sendThreadReply,
  setMailThreadLabels,
  trashThread,
  untrashThread,
  type GmailThreadContextApi,
  type MailAccountApi,
  type MailDraftApi,
  type MailThreadRowApi,
  type ThreadMessageApi,
} from '../lib/api';
import { useCockpitWidth } from '../hooks/useCockpitWidth';
import { readCockpitPlace, writeCockpitPlace } from '../lib/cockpitPlace';
import { emailSrcdoc, sanitizeEmailHtml, useEmailFrameHeight } from '../lib/sanitizeEmailHtml';
import './Mail.css';

// H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2) — "the last route and scroll
// offset of each column's screen are kept"; this screen keeps no hash
// sub-route of its own (`#/mail` is bare — see routes/route.ts), so its
// OWN "route" is which account/label/thread was open, plus the list's own
// scroll offset. Walk: open Mail, open a message, go home, re-enter Mail
// — the same message is open, the list scrolled where it was.
interface MailPlace {
  account: string | null;
  label: string;
  threadId: string | null;
  listScrollTop: number;
}

function readMailPlace(): MailPlace | null {
  return readCockpitPlace<MailPlace>('mail');
}

export interface MailProps {
  tenantId: string;
  /** Settings > Connected accounts — the needs_reconnect row's own door. */
  onOpenConnectedAccounts: () => void;
  /** MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — App.tsx's relay
   * for the cockpit "+" on this screen: a bumped token (never a boolean —
   * a second tap while Compose is already open must still register once
   * it closes). Opens the Compose sheet on every bump. */
  composeRequestToken: number;
}

type AccountsLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; accounts: MailAccountApi[] };

type ThreadsLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; threads: MailThreadRowApi[] };

type DetailLoad =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; messages: ThreadMessageApi[]; gmail: GmailThreadContextApi; subject: string | null };

type FoldStage = 'accounts' | 'list' | 'message';

/** The recorded draft's live content — fetched when the reply sheet opens
 * on a thread that has one (GET /api/mail/drafts/:id). `idle` on a thread
 * with no draft, or before the sheet has been opened. */
type DraftFetch =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; draft: MailDraftApi }
  | { phase: 'error'; code: string; message: string };

const INBOX = 'INBOX';

function shortWhen(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const d = new Date(iso);
  if (ms < 86_400_000 && d.getDate() === new Date().getDate()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (ms < 6 * 86_400_000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function longWhen(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function initialsOf(name: string | null): string {
  const base = (name ?? '').trim();
  if (!base) return '?';
  return base
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** The server's own sentence — never a vaguer second copy (voice canon §2.4). */
function sentenceOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.serverMessage ?? err.message;
  return fallback;
}

/** MAIL-POLISH BRIEF — Forward's own subject convention, same shape as the
 * reply sheet's inline "Re: " (that one is hardcoded in JSX too — this
 * mirrors it rather than introducing the one case that reaches into core
 * for a one-line string). */
/** A subject as a receipt names it: "(no subject)" when empty, an ellipsis when long. */
function receiptSubject(subject: string | null | undefined): string {
  const s = (subject ?? '').trim();
  if (!s) return '(no subject)';
  return s.length > 48 ? `${s.slice(0, 47).trimEnd()}…` : s;
}

function fwSubjectOf(subject: string | null): string {
  const s = (subject ?? '').trim();
  if (!s) return 'Fwd: (no subject)';
  return /^fwd:/i.test(s) ? s : `Fwd: ${s}`;
}

/** The quoted block under a forward's own note — Gmail's own convention
 * ("---------- Forwarded message ---------"), plain text (the forward's
 * OWN recorded draft is sent as text/plain, same as a coded reply). */
function quotedForward(m: ThreadMessageApi, subject: string | null): string {
  return [
    '---------- Forwarded message ---------',
    `From: ${m.senderIdentity ?? m.account?.email ?? '(unknown)'}`,
    `Date: ${longWhen(m.occurredAt)}`,
    `Subject: ${subject ?? '(no subject)'}`,
    '',
    m.bodyFormat === 'html' ? stripHtmlForQuote(m.body) : m.body,
  ].join('\n');
}

/** A rough plain-text rendering of an HTML body for the quoted block only
 * (never rendered as markup — this is text going into a textarea and then
 * a text/plain MIME part, so "rough" costs nothing a sanitizer would need
 * to fix: nothing here is ever parsed back as HTML). */
function stripHtmlForQuote(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>|<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** MAIL-POLISH BRIEF (2026-10-16, Huan's own words: "render the email
 * properly (HTML, links, images)") — one message's HTML body: sanitized
 * (lib/sanitizeEmailHtml.ts), rendered inside a sandboxed iframe via
 * `srcdoc` (NO allow-scripts, NO allow-same-origin — the brief's own exact
 * sandbox value), auto-height measured on a hidden parent-side clone (that
 * file's own header explains why: the sandbox's own flags make the
 * iframe's document unreadable from here). Remote images stay blocked
 * until "Show images" — PER MESSAGE, never a wider switch. */
function EmailBody({
  messageId,
  html,
  showImages,
  onShowImages,
}: {
  messageId: string;
  html: string;
  showImages: boolean;
  onShowImages: () => void;
}) {
  const sanitized = useMemo(() => sanitizeEmailHtml(html, showImages), [html, showImages]);
  const hasBlockedImages = useMemo(() => !showImages && /data-blocked-src=/.test(sanitized), [sanitized, showImages]);
  const srcdoc = useMemo(() => emailSrcdoc(sanitized, showImages), [sanitized, showImages]);
  const { onMeasureLoad, height } = useEmailFrameHeight();
  return (
    <div class="mail__html-body">
      {hasBlockedImages ? (
        <button type="button" class="mail__show-images" onClick={onShowImages}>
          Show images
        </button>
      ) : null}
      {/* Hidden measuring twin of the frame below — srcdoc only, never innerHTML; see
          useEmailFrameHeight for why it may be same-origin (no allow-scripts: inert). */}
      <iframe
        class="mail__html-measure"
        aria-hidden="true"
        tabIndex={-1}
        sandbox="allow-same-origin"
        srcdoc={srcdoc}
        onLoad={onMeasureLoad}
        title=""
      />
      <iframe
        class="mail__html-frame"
        // ⚑ EXACTLY the reviewed sandbox value — NO allow-scripts, NO allow-same-origin. It is not
        // empty on purpose: a fully empty sandbox stops links opening in a new tab at all
        // (allow-popups is what lets a rewritten target=_blank anchor leave the frame;
        // allow-popups-to-escape-sandbox lets the opened page run unsandboxed instead of
        // inheriting this one). A test pins the string so a future edit cannot widen it quietly.
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcdoc={srcdoc}
        style={{ height: `${height}px`, width: '100%', border: 'none', display: 'block' }}
        title="Message content"
        data-testid={`mail-html-frame-${messageId}`}
      />
    </div>
  );
}

export function Mail({ tenantId, onOpenConnectedAccounts, composeRequestToken }: MailProps) {
  const cockpitWidth = useCockpitWidth();
  const folded = cockpitWidth === 'bar';

  const [accountsLoad, setAccountsLoad] = useState<AccountsLoad>({ phase: 'loading' });
  const [openAccounts, setOpenAccounts] = useState<Record<string, boolean>>({});
  // H2 — restored from the kept place, when there is one; a fresh tenant
  // (or a store that threw/had nothing) reads exactly the old defaults.
  const [selAccount, setSelAccount] = useState<string | null>(() => readMailPlace()?.account ?? null);
  const [selLabel, setSelLabel] = useState<string>(() => readMailPlace()?.label ?? INBOX);
  const [threadsLoad, setThreadsLoad] = useState<ThreadsLoad>({ phase: 'loading' });
  const [selThreadId, setSelThreadId] = useState<string | null>(() => readMailPlace()?.threadId ?? null);
  const [detail, setDetail] = useState<DetailLoad>({ phase: 'idle' });
  const [replyOpen, setReplyOpen] = useState(false);
  // MAIL-POLISH BRIEF — Forward opens the SAME sheet in a second mode
  // (`replyOpen` just means "the sheet is open"; this says which shape it
  // takes). Reset to 'reply' whenever the sheet closes, so a stale
  // 'forward' never leaks into the next thread's plain Reply tap.
  const [composerMode, setComposerMode] = useState<'reply' | 'forward'>('reply');
  const [fwTo, setFwTo] = useState('');
  // Mirrors composeDraftId's own reason below: a Send that saves fine but
  // is then refused (the cap, a suppression) must RETRY the same draft on
  // a second tap, never silently mint a second orphan in Gmail Drafts.
  const [fwDraftId, setFwDraftId] = useState<string | null>(null);
  // Attachment file ids the person REMOVED from the forward before sending (chips are removable).
  const [fwRemoved, setFwRemoved] = useState<Set<string>>(new Set());
  const [replyBody, setReplyBody] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [draftFetch, setDraftFetch] = useState<DraftFetch>({ phase: 'idle' });
  const [actionError, setActionError] = useState<string | null>(null);
  // MAIL-POLISH BRIEF — Delete's own receipt ("Moved “<subject>” to Trash · Undo"),
  // separate from `actionError` (a refusal) — both can never be true at
  // once (a successful trash clears any stale actionError; a refused one
  // never sets a receipt), but they are two different slots in the voice
  // canon's own terms (a receipt names what happened; actionError names a
  // refusal) and are rendered differently below.
  const [trashReceipt, setTrashReceipt] = useState<{ threadId: string; subject: string } | null>(null);
  // Undo's own receipt line — names the conversation too (voice canon §2 rule 1).
  const [undoLine, setUndoLine] = useState<string | null>(null);
  // Remote images stay blocked per message until HE taps "Show images" —
  // never a thread-wide or app-wide switch (the brief's own words: "per
  // message"). Keyed by message id; survives a re-render of the same
  // thread, cleared only by leaving it (the effect below, on selThreadId).
  const [showImagesFor, setShowImagesFor] = useState<Set<string>>(new Set());
  // MAIL-POLISH BRIEF addition — Compose: the cockpit "+"'s own sheet, with
  // no thread underneath it at all (unlike reply/forward above). A SEPARATE
  // state slice rather than overloading replyOpen/composerMode: Compose can
  // open regardless of what thread (if any) is selected, and must not be
  // discarded by the "selThreadId changed" effect that resets the reply
  // sheet below.
  const [moreOpen, setMoreOpen] = useState(false);
  // How many of Delete/Archive stay as buttons at the reading pane's CURRENT width (4 = both,
  // 3 = Delete only, 2 = neither). A label never wraps and a button never shrinks (atlas-core
  // review, round 2): what does not fit moves into "More". Widths are the buttons' own, fixed by
  // their nowrap labels (Reply 68, Forward 86, Delete 73, Archive 82, More 65, 8px gaps).
  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const [actionSlots, setActionSlots] = useState<2 | 3 | 4>(4);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAccountId, setComposeAccountId] = useState<string | null>(null);
  const [composeDraftId, setComposeDraftId] = useState<string | null>(null);
  const [composeShownHash, setComposeShownHash] = useState<string | null>(null);
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const lastComposeToken = useRef(composeRequestToken);
  // The folded flow's own stage picks up where the kept place left off too
  // — a thread open restores straight to the 'message' pane, never back
  // behind the accounts list it would otherwise always start on.
  const [stage, setStage] = useState<FoldStage>(() => {
    const place = readMailPlace();
    if (!place) return 'accounts';
    return place.threadId ? 'message' : 'list';
  });
  const markedRead = useRef<Set<string>>(new Set());
  const listScrollRef = useRef<HTMLDivElement>(null);
  const placeScrollApplied = useRef(false);

  // Accounts — read once; reconnect/revoke happens on Settings, not here.
  useEffect(() => {
    let cancelled = false;
    setAccountsLoad({ phase: 'loading' });
    getMailAccounts(tenantId)
      .then((accounts) => {
        if (!cancelled) setAccountsLoad({ phase: 'ready', accounts });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAccountsLoad({ phase: 'error', message: sentenceOf(err, 'the mail accounts could not be read') });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // MAIL-POLISH BRIEF addition — App.tsx bumps `composeRequestToken` on
  // every "+" tap while this screen is in view; a changed token (never the
  // FIRST value seen — the ref starts at exactly what the prop already
  // was, so mounting the screen never opens Compose on its own) opens a
  // fresh Compose sheet, From defaulting to the account in view (or the
  // first one), every other field blank.
  useEffect(() => {
    if (composeRequestToken === lastComposeToken.current) return;
    lastComposeToken.current = composeRequestToken;
    setComposeOpen(true);
    setComposeTo('');
    setComposeCc('');
    setComposeSubject('');
    setComposeBody('');
    setComposeDraftId(null);
    setComposeShownHash(null);
    setComposeError(null);
    setComposeAccountId(
      selAccount ?? (accountsLoad.phase === 'ready' ? accountsLoad.accounts[0]?.connectionId ?? null : null)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selAccount/accountsLoad read for the default From only, at the moment of the tap
  }, [composeRequestToken]);

  // The list — re-read on account/label change, or after an optimistic
  // action is rolled back (reloadKey below is folded into the dep array
  // via the two setters already in it; a rollback re-sets `threadsLoad`
  // directly instead of re-fetching, so no extra key is needed here).
  useEffect(() => {
    let cancelled = false;
    setThreadsLoad({ phase: 'loading' });
    getMailThreads(tenantId, { account: selAccount ?? undefined, label: selLabel })
      .then(({ threads }) => {
        if (!cancelled) setThreadsLoad({ phase: 'ready', threads });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setThreadsLoad({ phase: 'error', message: sentenceOf(err, 'the message list could not be read') });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, selAccount, selLabel]);

  // H2 — keep the place: every meaningful change (a different account,
  // folder, or open thread) writes it straight back out. Runs on mount too
  // (writing back the SAME account/label/thread the lazy initial state
  // above just read) — harmless for those three, which have no async gap
  // between "restored" and "current" (they ARE the state from the first
  // render). `listScrollTop` is different: BEFORE the scroll-restore
  // effect below has had its one chance (`placeScrollApplied.current`
  // still false), the real scroll offset hasn't been applied to the DOM
  // yet, so `listScrollRef.current.scrollTop` reads 0 — writing that
  // through here on mount would clobber a genuinely kept offset before it
  // was ever read back. Keeping whatever is ALREADY on record for that
  // field until restoration has actually run is what that effect reads.
  useEffect(() => {
    writeCockpitPlace('mail', {
      account: selAccount,
      label: selLabel,
      threadId: selThreadId,
      listScrollTop: placeScrollApplied.current ? listScrollRef.current?.scrollTop ?? 0 : readMailPlace()?.listScrollTop ?? 0,
    });
  }, [selAccount, selLabel, selThreadId]);

  // The list's own scroll offset restores ONCE the real rows exist to
  // scroll to (never before — nothing to apply it to) — and only the
  // FIRST time, guarded by the ref: a later account/label change also
  // flips `threadsLoad` back to 'ready', and that refetch's own (correctly
  // reset-to-top) scroll must never be clobbered by the stale snapshot
  // this mount started with.
  useEffect(() => {
    if (placeScrollApplied.current || threadsLoad.phase !== 'ready') return;
    placeScrollApplied.current = true;
    const place = readMailPlace();
    if (listScrollRef.current && typeof place?.listScrollTop === 'number') {
      listScrollRef.current.scrollTop = place.listScrollTop;
    }
  }, [threadsLoad]);

  const actionBarOpen = detail.phase === 'ready';
  useEffect(() => {
    const bar = actionBarRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const fit = () => {
      const w = bar.clientWidth;
      setActionSlots(w === 0 || w >= 420 ? 4 : w >= 336 ? 3 : 2); // 0 = not laid out (jsdom/happy-dom): show all
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [actionBarOpen, selThreadId]);

  // The open thread's messages + gmail context.
  useEffect(() => {
    if (!selThreadId) {
      setDetail({ phase: 'idle' });
      return;
    }
    let cancelled = false;
    setDetail({ phase: 'loading' });
    setReplyOpen(false);
    setComposerMode('reply');
    setFwTo('');
    setFwDraftId(null);
    setFwRemoved(new Set());
    setReplyBody('');
    setReplyError(null);
    setDraftFetch({ phase: 'idle' });
    setShowImagesFor(new Set());
    getThread(tenantId, selThreadId)
      .then(({ thread, messages, gmail }) => {
        if (cancelled) return;
        setDetail({ phase: 'ready', messages, gmail, subject: thread.subject });
        // Opening an unread thread marks it read — see this file's own
        // header for why there is no separate drawn control for this.
        const row = threadsLoad.phase === 'ready' ? threadsLoad.threads.find((t) => t.threadId === selThreadId) : undefined;
        if (row?.unread && !markedRead.current.has(selThreadId)) {
          markedRead.current.add(selThreadId);
          void applyLabelChange(selThreadId, { remove: ['UNREAD'] }, { silent: true });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail({ phase: 'error', message: sentenceOf(err, 'this conversation could not be read') });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- threadsLoad read for the mark-read side effect only
  }, [tenantId, selThreadId]);

  // The recorded draft's live content (core/mail-draft-read) — fetched the
  // moment the reply sheet opens on a thread that has one, never before:
  // reading it is wasted work on every thread without a draft, which is
  // most of them. Prefills the composer ONLY while it is still empty —
  // never clobbers text the person has already started typing while this
  // read was in flight.
  const draftId = detail.phase === 'ready' ? detail.gmail.draft?.id : undefined;
  useEffect(() => {
    if (!replyOpen || !draftId) return;
    let cancelled = false;
    setDraftFetch({ phase: 'loading' });
    getMailDraft(tenantId, draftId)
      .then(({ draft }) => {
        if (cancelled) return;
        setDraftFetch({ phase: 'ready', draft });
        setReplyBody((cur) => (cur === '' ? draft.body : cur));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code = err instanceof ApiError ? err.code : 'unknown_error';
        setDraftFetch({ phase: 'error', code, message: sentenceOf(err, 'the draft could not be read') });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, replyOpen, draftId]);

  function reloadDraft() {
    if (draftId) setDraftFetch({ phase: 'idle' });
    // Re-opening the effect above by toggling replyOpen would also discard
    // the composer; instead re-run the SAME fetch directly.
    if (!draftId) return;
    setDraftFetch({ phase: 'loading' });
    getMailDraft(tenantId, draftId)
      .then(({ draft }) => {
        setDraftFetch({ phase: 'ready', draft });
        setReplyBody(draft.body);
      })
      .catch((err: unknown) => {
        const code = err instanceof ApiError ? err.code : 'unknown_error';
        setDraftFetch({ phase: 'error', code, message: sentenceOf(err, 'the draft could not be read') });
      });
  }

  /** Optimistic: flips the row (and the open thread, if it's this one)
   * first, then calls the server; a refusal rolls the row back and names
   * the server's own sentence (voice canon — receipts, not acknowledgements). */
  async function applyLabelChange(
    threadId: string,
    change: { add?: string[]; remove?: string[] },
    opts: { silent?: boolean } = {}
  ): Promise<void> {
    if (threadsLoad.phase !== 'ready') return;
    const before = threadsLoad.threads;
    const next = before.map((t) => {
      if (t.threadId !== threadId) return t;
      let unread = t.unread;
      if (change.remove?.includes('UNREAD')) unread = false;
      if (change.add?.includes('UNREAD')) unread = true;
      return change.remove?.includes('INBOX') ? null : { ...t, unread };
    });
    const withoutArchived = next.filter((t): t is MailThreadRowApi => t !== null);
    setThreadsLoad({ phase: 'ready', threads: withoutArchived });
    try {
      await setMailThreadLabels(tenantId, threadId, change);
      if (!opts.silent) setActionError(null);
    } catch (err) {
      setThreadsLoad({ phase: 'ready', threads: before });
      setActionError(sentenceOf(err, 'that change did not go through'));
    }
  }

  function openThread(id: string) {
    setSelThreadId(id);
    setStage('message');
  }

  function pickAccount(id: string | null) {
    setSelAccount(id);
    setSelLabel(INBOX);
    setSelThreadId(null);
    setStage('list');
  }

  function pickLabel(accountId: string, labelId: string) {
    setSelAccount(accountId);
    setSelLabel(labelId);
    setSelThreadId(null);
    setStage('list');
  }

  function toggleAccountOpen(id: string) {
    setOpenAccounts((o) => ({ ...o, [id]: !o[id] }));
  }

  async function sendReply() {
    if (!selThreadId || detail.phase !== 'ready' || !replyBody.trim()) return;
    setReplyBusy(true);
    setReplyError(null);
    try {
      await sendThreadReply(tenantId, selThreadId, replyBody.trim());
      setReplyOpen(false);
      setReplyBody('');
      // The sent reply is the thread's new latest message — re-read it.
      const { messages, gmail } = await getThread(tenantId, selThreadId);
      setDetail({ phase: 'ready', messages, gmail, subject: detail.subject });
    } catch (err) {
      // The typed text STAYS — a refusal must never cost the words
      // (voice canon: "keep the typed text on every refusal").
      setReplyError(sentenceOf(err, 'the reply was not sent'));
    } finally {
      setReplyBusy(false);
    }
  }

  /** "Forward" opens the SAME sheet in forward mode — see `composerMode`'s
   * own comment. To starts empty and editable (never the thread's inbound
   * sender — Forward is a new conversation with its own audience); Subject
   * and the quoted original prefill from the thread's own latest message. */
  function openForward() {
    if (detail.phase !== 'ready') return;
    setComposerMode('forward');
    setFwTo('');
    setFwDraftId(null);
    setFwRemoved(new Set());
    setReplyBody('');
    setReplyError(null);
    setReplyOpen(true);
  }

  /** Forward's own Send — a RECORDED draft exactly like reply (judgement
   * call, reported in the builder's final message): it goes through the
   * SAME no-threadId compose primitives Compose uses
   * (saveMailCompose -> his tap -> sendMailCompose) rather than the
   * thread-keyed reply slot, because `gmail_drafts` holds at most ONE open
   * draft per (account, thread) and that slot may already be a REPLY draft
   * (Atlas's own, or his) — overwriting it with Forward's content on a
   * save would silently discard a different draft he had not touched yet.
   * Mechanically identical otherwise: saveGmailDraft -> Send -> sendGmailDraft,
   * same cap/refusals/receipt, never auto-sent. From is still locked to the
   * thread's own account (the brief's own words) — just reached through
   * Compose's connectionId field instead of the reply slot's implicit one. */
  async function sendForward() {
    if (detail.phase !== 'ready' || !detail.gmail.replyFrom) return;
    const toList = fwTo.split(',').map((s) => s.trim()).filter(Boolean);
    if (!toList.length) {
      setReplyError('Add at least one recipient.');
      return;
    }
    const last = detail.messages[detail.messages.length - 1];
    if (!last) return;
    setReplyBusy(true);
    setReplyError(null);
    try {
      const note = replyBody.trim();
      const body = `${note ? `${note}\n\n` : ''}${quotedForward(last, detail.subject)}`;
      const saved = await saveMailCompose(tenantId, {
        connectionId: detail.gmail.replyFrom.connectionId,
        to: toList,
        subject: fwSubjectOf(detail.subject),
        body,
        draftId: fwDraftId ?? undefined,
        attachmentFileIds: (last.attachments ?? []).map((a) => a.fileId).filter((id) => !fwRemoved.has(id)),
        forwardOfThreadId: last.threadId,
      });
      setFwDraftId(saved.draft.draftId);
      await sendMailCompose(tenantId, saved.draft.draftId, saved.draft.shownHash);
      setReplyOpen(false);
      setComposerMode('reply');
      setFwTo('');
      setFwDraftId(null);
      setReplyBody('');
    } catch (err) {
      // The typed text and the To field STAY on a refusal — same rule
      // reply's own send follows (voice canon).
      setReplyError(sentenceOf(err, 'the message was not sent'));
    } finally {
      setReplyBusy(false);
    }
  }

  /** Delete (reading pane + list row, MAIL-POLISH BRIEF): optimistic
   * remove, a receipt with Undo, the server's own sentence on a refusal
   * (same `applyLabelChange` shape as Archive above). NOT an atlas-cockpit
   * verb — see this screen's own header and the server route's. */
  async function deleteThread(threadId: string) {
    if (threadsLoad.phase !== 'ready') return;
    const before = threadsLoad.threads;
    const subject = receiptSubject(before.find((t) => t.threadId === threadId)?.subject);
    setThreadsLoad({ phase: 'ready', threads: before.filter((t) => t.threadId !== threadId) });
    if (selThreadId === threadId) setSelThreadId(null);
    setActionError(null);
    setUndoLine(null);
    try {
      await trashThread(tenantId, threadId);
      setTrashReceipt({ threadId, subject });
    } catch (err) {
      setThreadsLoad({ phase: 'ready', threads: before });
      setActionError(sentenceOf(err, 'that could not be moved to Trash'));
    }
  }

  /** Undo's own door (threads.untrash) — restores the row; the list is
   * re-read rather than spliced back in by hand, since Undo may land after
   * other rows have already shifted (a later message arrived, another row
   * was archived) and a hand-spliced row would land in the wrong place. */
  async function undoTrash() {
    if (!trashReceipt) return;
    const { threadId, subject } = trashReceipt;
    setTrashReceipt(null);
    try {
      await untrashThread(tenantId, threadId);
      // Untrash restores INBOX (core's trash mirror), so that is where it is back.
      setUndoLine(`“${subject}” is back in Inbox`);
      const { threads } = await getMailThreads(tenantId, { account: selAccount ?? undefined, label: selLabel });
      setThreadsLoad({ phase: 'ready', threads });
    } catch (err) {
      setActionError(sentenceOf(err, 'that could not be restored'));
    }
  }

  /** Compose's own Send (MAIL-POLISH BRIEF addition) — saves the recorded
   * draft (creating it the first time, updating the SAME one after via
   * `composeDraftId`) then sends it in the one tap; never auto-sent, same
   * cap/refusals as reply. */
  async function sendCompose() {
    if (!composeAccountId) {
      setComposeError('Pick an account to send from.');
      return;
    }
    const toList = composeTo.split(',').map((s) => s.trim()).filter(Boolean);
    if (!toList.length) {
      setComposeError('Add at least one recipient.');
      return;
    }
    const ccList = composeCc.split(',').map((s) => s.trim()).filter(Boolean);
    setComposeBusy(true);
    setComposeError(null);
    try {
      const saved = await saveMailCompose(tenantId, {
        connectionId: composeAccountId,
        to: toList,
        cc: ccList.length ? ccList : undefined,
        subject: composeSubject.trim(),
        body: composeBody,
        draftId: composeDraftId ?? undefined,
      });
      setComposeDraftId(saved.draft.draftId);
      setComposeShownHash(saved.draft.shownHash);
      await sendMailCompose(tenantId, saved.draft.draftId, saved.draft.shownHash);
      setComposeOpen(false);
    } catch (err) {
      // The typed fields STAY on a refusal — same rule every send here follows.
      setComposeError(sentenceOf(err, 'the message was not sent'));
    } finally {
      setComposeBusy(false);
    }
  }

  if (accountsLoad.phase === 'loading') {
    return (
      <div class="mail">
        <div class="mail__loading" aria-hidden="true">
          <div class="mail__skeleton-row" />
          <div class="mail__skeleton-row" />
          <div class="mail__skeleton-row" />
        </div>
      </div>
    );
  }

  if (accountsLoad.phase === 'error') {
    return (
      <div class="mail">
        <div class="mail__say">
          Mail could not be read.
          <br />
          <span class="mail__say-soft">{accountsLoad.message}</span>
        </div>
      </div>
    );
  }

  const { accounts } = accountsLoad;

  if (accounts.length === 0) {
    return (
      <div class="mail">
        <div class="mail__say">
          No account is sending mail here yet.
          <br />
          <button type="button" class="mail__say-link" onClick={onOpenConnectedAccounts}>
            Connect one in Settings › Connected accounts
          </button>
        </div>
      </div>
    );
  }

  function accountUnread(acc: MailAccountApi): number {
    return acc.labels.find((l) => l.id === INBOX)?.unread ?? 0;
  }
  const allUnread = accounts.reduce((n, a) => n + accountUnread(a), 0);

  const accountsPane = (
    <nav aria-label="Accounts" class="mail__accounts strip-mist">
      <button
        type="button"
        class={`mail__account-row${selAccount === null ? ' is-selected' : ''}`}
        onClick={() => pickAccount(null)}
      >
        <span class="mail__account-dot" style={{ background: 'var(--mist-dim)' }} aria-hidden="true" />
        <span class="mail__account-label">All inboxes</span>
        {allUnread > 0 ? <span class="mail__account-unread">{allUnread}</span> : null}
      </button>
      {accounts.map((acc) => {
        const needsReconnect = acc.status !== 'active';
        const open = !!openAccounts[acc.connectionId];
        const unread = accountUnread(acc);
        return (
          <div class="mail__account" key={acc.connectionId}>
            <div class={`mail__account-row-wrap${selAccount === acc.connectionId ? ' is-selected' : ''}`}>
              <button
                type="button"
                class="mail__account-chevron"
                aria-label={`${open ? 'Hide' : 'Show'} folders for ${acc.email}`}
                aria-expanded={open}
                onClick={() => toggleAccountOpen(acc.connectionId)}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
                  <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button type="button" class="mail__account-row" onClick={() => pickAccount(acc.connectionId)}>
                <span class="mail__account-label">{acc.email}</span>
                {needsReconnect ? (
                  <span class="mail__account-reconnect" role="status">Reconnect</span>
                ) : unread > 0 ? (
                  <span class="mail__account-unread">{unread}</span>
                ) : null}
              </button>
            </div>
            {needsReconnect ? (
              <button type="button" class="mail__reconnect-link" onClick={onOpenConnectedAccounts}>
                Reconnect {acc.email} — Settings › Connected accounts
              </button>
            ) : null}
            {open && acc.labels.length > 0 ? (
              <div class="mail__labels">
                {acc.labels.map((l) => (
                  <button
                    type="button"
                    key={l.id}
                    class={`mail__label-row${selAccount === acc.connectionId && selLabel === l.id ? ' is-selected' : ''}`}
                    onClick={() => pickLabel(acc.connectionId, l.id)}
                  >
                    <span class="mail__label-name">{l.name}</span>
                    {l.unread ? <span class="mail__label-count">{l.unread}</span> : l.total ? <span class="mail__label-count">{l.total}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );

  const listTitle = selAccount === null ? 'All inboxes' : accounts.find((a) => a.connectionId === selAccount)?.labels.find((l) => l.id === selLabel)?.name ?? selLabel;
  const listSub =
    threadsLoad.phase === 'ready'
      ? `${threadsLoad.threads.filter((t) => t.unread).length} unread`
      : '';

  function threadRow(t: MailThreadRowApi) {
    const isSel = t.threadId === selThreadId;
    return (
      <button
        type="button"
        key={t.threadId}
        class={`mail__row${isSel ? ' is-selected' : ''}${t.unread ? ' is-unread' : ''}`}
        onClick={() => openThread(t.threadId)}
      >
        <span class="mail__row-top">
          <button
            type="button"
            class="mail__unread-dot"
            aria-label={t.unread ? 'Mark read' : 'Mark unread'}
            aria-pressed={t.unread}
            onClick={(e) => {
              e.stopPropagation();
              void applyLabelChange(t.threadId, t.unread ? { remove: ['UNREAD'] } : { add: ['UNREAD'] });
            }}
          />
          <span class="mail__row-from">{t.from ?? t.account.email}</span>
          <span class="mail__row-when">{shortWhen(t.lastMessageAt)}</span>
        </span>
        <span class="mail__row-subject">{t.subject ?? '(no subject)'}</span>
        <span class="mail__row-snippet">{t.snippet}</span>
        <span class="mail__row-chips">
          {selAccount === null ? <span class="mail__chip">{t.account.email}</span> : null}
          {t.hasDraft ? <span class="mail__chip mail__chip--draft">Draft ready</span> : null}
          {t.phishing ? <span class="mail__chip mail__chip--flag">Likely phishing</span> : null}
          <button
            type="button"
            class="mail__row-archive"
            onClick={(e) => {
              e.stopPropagation();
              void applyLabelChange(t.threadId, { remove: ['INBOX'] });
              if (selThreadId === t.threadId) setSelThreadId(null);
            }}
          >
            Archive
          </button>
          <button
            type="button"
            class="mail__row-delete"
            onClick={(e) => {
              e.stopPropagation();
              void deleteThread(t.threadId);
            }}
          >
            Delete
          </button>
        </span>
      </button>
    );
  }

  const listPane = (
    <section aria-label="Messages" class="mail__list">
      <header class="mail__list-header">
        <h1 class="mail__list-title">{listTitle}</h1>
        <span class="mail__list-sub">{listSub}</span>
      </header>
      {actionError ? (
        <div class="mail__action-error" role="alert">
          {actionError}
        </div>
      ) : null}
      {trashReceipt ? (
        <div class="mail__receipt" role="status">
          <span>Moved “{trashReceipt.subject}” to Trash</span>
          <button type="button" class="mail__receipt-undo" onClick={() => void undoTrash()}>
            Undo
          </button>
        </div>
      ) : null}
      {undoLine && !trashReceipt ? (
        <div class="mail__receipt" role="status">
          <span>{undoLine}</span>
        </div>
      ) : null}
      <div
        class="mail__list-scroll"
        ref={listScrollRef}
        onScroll={() => {
          writeCockpitPlace('mail', {
            account: selAccount,
            label: selLabel,
            threadId: selThreadId,
            listScrollTop: listScrollRef.current?.scrollTop ?? 0,
          });
        }}
      >
        {threadsLoad.phase === 'loading' ? (
          <div class="mail__skeleton-row" aria-hidden="true" />
        ) : threadsLoad.phase === 'error' ? (
          <div class="mail__say">
            The list could not be read.
            <br />
            <span class="mail__say-soft">{threadsLoad.message}</span>
          </div>
        ) : threadsLoad.threads.length === 0 ? (
          <div class="mail__say">Nothing here.</div>
        ) : (
          threadsLoad.threads.map(threadRow)
        )}
      </div>
    </section>
  );

  function replySheet(sel: {
    subject: string | null;
    replyFrom: GmailThreadContextApi['replyFrom'];
    draft: GmailThreadContextApi['draft'];
    to: string | null;
    /** The thread's own latest message — Forward's own quoted block reads
     * this directly rather than reaching into `detail` from inside this
     * function (a plain function, not the type-narrowed IIFE its one call
     * site lives in). */
    lastMessage: ThreadMessageApi | null;
  }) {
    // The note under the composer — always ONE line, never stacked with
    // the refusal above it (replyError already owns that slot). Order of
    // precedence: a send refusal > a draft-read refusal > the draft's own
    // state > a plain new reply.
    const note = replyError
      ? null
      : draftFetch.phase === 'loading'
        ? 'Loading the saved draft…'
        : draftFetch.phase === 'error'
          ? null // rendered as its own refusal block below, with Reload
          : sel.draft
            ? draftFetch.phase === 'ready' && draftFetch.draft.changedInGmail
              ? 'Edited in Gmail'
              : sel.draft.origin === 'atlas'
                ? 'Drafted by Atlas · saved to Gmail Drafts'
                : 'Draft saved to Gmail Drafts'
            : 'New reply';
    const isForward = composerMode === 'forward';
    return (
      <div class="mail__reply-sheet">
        <div class="mail__reply-fields">
          <span class="mail__reply-field-label">To</span>
          {isForward ? (
            <input
              type="text"
              class="mail__reply-field-input"
              placeholder="name@example.invalid"
              value={fwTo}
              disabled={replyBusy}
              onInput={(e) => setFwTo((e.target as HTMLInputElement).value)}
              aria-label="To"
            />
          ) : (
            <span class="mail__reply-field-value">{sel.to ?? '—'}</span>
          )}
          <span class="mail__reply-field-label">From</span>
          <span class="mail__reply-field-value">{sel.replyFrom?.email ?? '—'}</span>
          <span class="mail__reply-field-label">Subject</span>
          <span class="mail__reply-field-value">{isForward ? fwSubjectOf(sel.subject) : `Re: ${sel.subject ?? '(no subject)'}`}</span>
        </div>
        <label for="mail-reply-body" class="mail__sr-only">
          {isForward ? 'A note above the forwarded message' : 'Reply'}
        </label>
        <textarea
          id="mail-reply-body"
          class="mail__reply-body"
          placeholder={isForward ? 'Add a note (optional)' : undefined}
          value={replyBody}
          disabled={draftFetch.phase === 'loading'}
          onInput={(e) => setReplyBody((e.target as HTMLTextAreaElement).value)}
        />
        {isForward && sel.lastMessage ? (
          <div class="mail__reply-quoted" aria-label="Forwarded message">
            {quotedForward(sel.lastMessage, sel.subject)}
          </div>
        ) : null}
        {isForward && sel.lastMessage?.attachments?.length ? (
          <div class="mail__fw-attachments" aria-label="Attachments">
            {sel.lastMessage.attachments.map((a) =>
              fwRemoved.has(a.fileId) ? null : (
                <span class="mail__fw-chip" key={a.fileId}>
                  <span class="mail__fw-chip-name">{a.name}</span>
                  <button
                    type="button"
                    class="mail__fw-chip-x"
                    aria-label={`Remove ${a.name}`}
                    disabled={replyBusy}
                    onClick={() => setFwRemoved((r) => new Set(r).add(a.fileId))}
                  >
                    ×
                  </button>
                </span>
              )
            )}
          </div>
        ) : null}
        {replyError ? (
          <div class="mail__reply-error" role="alert">
            {replyError}
          </div>
        ) : draftFetch.phase === 'error' ? (
          <div class="mail__reply-error" role="alert">
            {draftFetch.message}
            {draftFetch.code === 'draft_changed' ? (
              <button type="button" class="mail__reply-reload" onClick={reloadDraft}>
                Reload
              </button>
            ) : null}
          </div>
        ) : (
          <div class="mail__reply-note">{isForward ? null : note}</div>
        )}
        <div class="mail__reply-actions">
          <button
            type="button"
            class="mail__btn"
            onClick={() => {
              setReplyOpen(false);
              setComposerMode('reply');
            }}
            disabled={replyBusy}
          >
            Discard
          </button>
          <button
            type="button"
            class="mail__btn mail__btn--primary"
            onClick={() => void (isForward ? sendForward() : sendReply())}
            disabled={replyBusy || (isForward ? !fwTo.trim() : !replyBody.trim()) || draftFetch.phase === 'loading'}
          >
            {replyBusy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    );
  }

  const selRow = threadsLoad.phase === 'ready' ? threadsLoad.threads.find((t) => t.threadId === selThreadId) : undefined;

  const readingPane = !selThreadId ? (
    <main class="mail__reading mail__reading--empty">
      <div class="mail__say mail__say-soft">Nothing selected.</div>
    </main>
  ) : detail.phase === 'loading' || detail.phase === 'idle' ? (
    <main class="mail__reading">
      <div class="mail__skeleton-row" aria-hidden="true" />
    </main>
  ) : detail.phase === 'error' ? (
    <main class="mail__reading">
      <div class="mail__say">
        This conversation could not be read.
        <br />
        <span class="mail__say-soft">{detail.message}</span>
      </div>
    </main>
  ) : (
    (() => {
      const last = detail.messages[detail.messages.length - 1] ?? null;
      const flagged = last ? last.phishing.length > 0 : false;
      // The reply sheet's To: the correspondent, not us — the last INBOUND
      // message's sender, same convention the mockup draws ("To" =
      // {{sel.fromAddr}}, read-only). null on a thread of only our own
      // sent mail (no inbound to reply to).
      const replyTo = [...detail.messages].reverse().find((m) => m.direction === 'inbound')?.senderIdentity ?? null;
      return (
        <main class="mail__reading">
          <div class="mail__reading-scroll">
            {detail.messages.map((m) => (
              <article class="mail__message" key={m.id}>
                <div class="mail__message-head">
                  <div class="mail__avatar" aria-hidden="true">
                    {initialsOf(m.senderIdentity)}
                  </div>
                  <div class="mail__message-meta">
                    <div class="mail__message-top">
                      <span class="mail__message-from">{m.senderIdentity ?? m.account?.email ?? '(unknown)'}</span>
                      <span class="mail__message-when">{longWhen(m.occurredAt)}</span>
                    </div>
                  </div>
                </div>
                {m.phishing.length > 0 ? (
                  <div class="mail__flag" role="note">
                    <strong>Likely phishing.</strong> {m.phishing[0]} No reply drafted, links not opened.
                  </div>
                ) : m.bodyFormat === 'html' ? (
                  <EmailBody
                    messageId={m.id}
                    html={m.body}
                    showImages={showImagesFor.has(m.id)}
                    onShowImages={() => setShowImagesFor((s) => new Set(s).add(m.id))}
                  />
                ) : (
                  <div class="mail__message-body">{m.body}</div>
                )}
              </article>
            ))}
          </div>
          <div class="mail__reading-actions" ref={actionBarRef}>
            <button type="button" class="mail__btn" onClick={() => setReplyOpen(true)} disabled={flagged}>
              {detail.gmail.draft ? 'Reply · draft ready' : 'Reply'}
            </button>
            <button type="button" class="mail__btn" onClick={openForward} disabled={flagged}>
              Forward
            </button>
            {actionSlots >= 3 ? (
              <button type="button" class="mail__btn" onClick={() => selThreadId && void deleteThread(selThreadId)}>
                Delete
              </button>
            ) : null}
            {actionSlots >= 4 ? (
              <button
                type="button"
                class="mail__btn"
                onClick={() => {
                  if (!selThreadId) return;
                  void applyLabelChange(selThreadId, { remove: ['INBOX'] });
                  setSelThreadId(null);
                }}
              >
                Archive
              </button>
            ) : null}
            {/* What does not fit at this width lives behind "More" — a label never wraps, a
                button never shrinks (atlas-core review, round 2). Mark read/unread always does. */}
            <div class="mail__more">
              <button
                type="button"
                class="mail__btn"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((o) => !o)}
              >
                More
              </button>
              {moreOpen ? (
                <div class="mail__more-menu" role="menu">
                  {actionSlots < 3 ? (
                    <button
                      type="button"
                      role="menuitem"
                      class="mail__more-item"
                      onClick={() => {
                        setMoreOpen(false);
                        if (selThreadId) void deleteThread(selThreadId);
                      }}
                    >
                      Delete
                    </button>
                  ) : null}
                  {actionSlots < 4 ? (
                    <button
                      type="button"
                      role="menuitem"
                      class="mail__more-item"
                      onClick={() => {
                        setMoreOpen(false);
                        if (!selThreadId) return;
                        void applyLabelChange(selThreadId, { remove: ['INBOX'] });
                        setSelThreadId(null);
                      }}
                    >
                      Archive
                    </button>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    class="mail__more-item"
                    onClick={() => {
                      setMoreOpen(false);
                      if (!selThreadId || !selRow) return;
                      void applyLabelChange(selThreadId, selRow.unread ? { remove: ['UNREAD'] } : { add: ['UNREAD'] });
                    }}
                  >
                    {selRow?.unread === false ? 'Mark unread' : 'Mark read'}
                  </button>
                </div>
              ) : null}
            </div>
            <span class="mail__reading-account">{detail.gmail.replyFrom?.email ?? ''}</span>
          </div>
          {replyOpen && !flagged
            ? replySheet({ subject: detail.subject, replyFrom: detail.gmail.replyFrom, draft: detail.gmail.draft, to: replyTo, lastMessage: last })
            : null}
        </main>
      );
    })()
  );

  // MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — Compose: a
  // standalone sheet, no thread underneath it, so it is built once here
  // and rendered on TOP of either layout below rather than living inside
  // readingPane/replySheet (which both assume a selected thread).
  const composeSheet = composeOpen ? (
    <div class="mail__compose-overlay" role="dialog" aria-label="New email">
      <div class="mail__compose-sheet">
        <h2 class="mail__compose-title">New email</h2>
        <div class="mail__compose-fields">
          <label class="mail__compose-label" for="mail-compose-from">
            From
          </label>
          <select
            id="mail-compose-from"
            class="mail__compose-from"
            value={composeAccountId ?? ''}
            disabled={composeBusy}
            onChange={(e) => setComposeAccountId((e.target as HTMLSelectElement).value)}
          >
            {accountsLoad.phase === 'ready'
              ? accountsLoad.accounts.map((a) => (
                  <option key={a.connectionId} value={a.connectionId}>
                    {a.email}
                  </option>
                ))
              : null}
          </select>
          <label class="mail__compose-label" for="mail-compose-to">
            To
          </label>
          <input
            id="mail-compose-to"
            type="text"
            class="mail__compose-to"
            placeholder="name@example.invalid"
            value={composeTo}
            disabled={composeBusy}
            onInput={(e) => setComposeTo((e.target as HTMLInputElement).value)}
          />
          <label class="mail__compose-label" for="mail-compose-cc">
            Cc
          </label>
          <input
            id="mail-compose-cc"
            type="text"
            class="mail__compose-cc"
            value={composeCc}
            disabled={composeBusy}
            onInput={(e) => setComposeCc((e.target as HTMLInputElement).value)}
          />
          <label class="mail__compose-label" for="mail-compose-subject">
            Subject
          </label>
          <input
            id="mail-compose-subject"
            type="text"
            class="mail__compose-subject"
            value={composeSubject}
            disabled={composeBusy}
            onInput={(e) => setComposeSubject((e.target as HTMLInputElement).value)}
          />
        </div>
        <label for="mail-compose-body" class="mail__sr-only">
          Message
        </label>
        <textarea
          id="mail-compose-body"
          class="mail__compose-body"
          value={composeBody}
          disabled={composeBusy}
          onInput={(e) => setComposeBody((e.target as HTMLTextAreaElement).value)}
        />
        {composeError ? (
          <div class="mail__reply-error" role="alert">
            {composeError}
          </div>
        ) : null}
        <div class="mail__compose-actions">
          <button
            type="button"
            class="mail__btn mail__compose-discard"
            onClick={() => setComposeOpen(false)}
            disabled={composeBusy}
          >
            Discard
          </button>
          <button
            type="button"
            class="mail__btn mail__btn--primary"
            onClick={() => void sendCompose()}
            disabled={composeBusy || !composeTo.trim() || !composeAccountId}
          >
            {composeBusy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (!folded) {
    return (
      <div class="mail mail--wide">
        {accountsPane}
        {listPane}
        {readingPane}
        {composeSheet}
      </div>
    );
  }

  // Folded: one pane at a time, with a back link up the chain. Compose is
  // a sibling of every stage (never nested in one) — the "+" that opens it
  // lives in the shell's own titlebar, outside this screen's fold stages
  // entirely, so a tap must open it no matter which stage is showing.
  return (
    <div class="mail mail--folded">
      {composeSheet}
      {stage === 'accounts' ? (
        <div class="mail__fold-pane">{accountsPane}</div>
      ) : stage === 'list' ? (
        <div class="mail__fold-pane">
          <div class="mail__fold-back">
            <button type="button" class="mail__back" onClick={() => setStage('accounts')}>
              ‹ Mail
            </button>
          </div>
          {listPane}
        </div>
      ) : (
        <div class="mail__fold-pane">
          <div class="mail__fold-back">
            <button type="button" class="mail__back" onClick={() => setStage('list')}>
              ‹ {listTitle}
            </button>
          </div>
          {readingPane}
        </div>
      )}
    </div>
  );
}
